import List from '@material-ui/core/List';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useAppSelector } from '../../store/hooks';
import { Section, Title, Topic } from '../../store/models';
import { _activeLanguageCode } from '../../store/slices/editorSlice';
import { _selectedTopicId } from '../../store/slices/topicSlice';
import { _organizations } from '../../store/slices/organizationSlice';
import { _locations } from '../../store/slices/locationSlice';
import { DroppableMenuWrapper } from '../ElementMenu/DroppableMenuWrapper';
import { PageMenuItem } from './PageMenuItem';
import { PageMenuItemAdd } from './PageMenuItemAdd';
import { useEffect, useRef, useMemo } from 'react';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		list: {
			paddingTop: 0,
			paddingBottom: 0,
		},
		selected: {
			margin: '1px 0px',
			boxShadow: '0 0 0.1em rgb(0 0 0 / 20%)',
		},
	})
);

export type PageMenuOption = {
	topicId: string;
	displayName: string;
};

export type PageMenuProps = {
	section: Section;
	locationId: string;
	orderedTopics: Topic[];
	disableDelete: boolean;
	dropDisabled?: boolean;
	showAddPage: boolean;
	setShowAddPage: (show: boolean) => void;
	renamePage: (topicId: string, title: Title) => void;
	reorderTopic: (payload: { oldIndex: number; oldSectionId: string; newIndex: number; newSectionId: string }) => void;
	addTopic: (pageName: string) => void;
};

const defaultProps = {
	dropDisabled: false,
	showAddPage: false,
};

export const PageMenu = (props: PageMenuProps) => {
	const {
		section,
		locationId,
		orderedTopics,
		disableDelete,
		dropDisabled,
		showAddPage,
		setShowAddPage,
		renamePage,
		reorderTopic,
		addTopic,
	} = {
		...defaultProps,
		...props,
	};

	const classes = useStyles();
	const selectedTopicId = useAppSelector(_selectedTopicId);
	const activeLanguageCode = useAppSelector(_activeLanguageCode);
	const locations = useAppSelector(_locations);
	const organizations = useAppSelector(_organizations);

	// Get the organization for this location
	const organization = useMemo(() => {
		const location = locations[locationId];
		if (!location?.organizationId) return null;
		return organizations[location.organizationId];
	}, [locations, locationId, organizations]);

	const selectedRef = useRef(null);
	useEffect(() => {
		if (selectedTopicId && selectedRef.current) {
			selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [selectedTopicId]);

	// Helper function to generate topic link
	const generateTopicLink = (topic: Topic): string => {
		const appSlug = organization?.appSlug;
		const pageSlug = topic?.metadata?.pageSlug;
		const baseUrl = process.env.REACT_APP_LIIINGO_WEBAPP_URL;

		console.log('appSlug', appSlug, 'pageSlug', pageSlug, 'baseUrl', baseUrl);
		// Use slug-based URL if both appSlug and pageSlug are available
		if (appSlug && pageSlug) {
			const url = `${baseUrl}/${appSlug}/${pageSlug}`;
			console.log('URL: returning', url);
			return url;
		}

		// Fall back to standard topic ID URL
		return `${baseUrl}/topic/${topic.id}`;
	};

	return (
		section && (
			<DroppableMenuWrapper id={section?.id} dropDisabled={dropDisabled} disableGutters>
				<List className={classes.list} component="div">
					{/* render topics in order */}
					{orderedTopics.map((topic, index) => {
						return (
							topic && (
								<div
									key={topic.id + '-page-menu-item-div'}
									className={topic.id === selectedTopicId ? classes.selected : null}
									ref={topic.id === selectedTopicId ? selectedRef : null}
								>
									{/* each page */}
									<PageMenuItem
										key={topic.id + '-page-menu-item'}
										locationId={locationId}
										status={topic.status}
										enableSharing={topic.enableSharing}
										topicId={topic.id}
										topicLink={generateTopicLink(topic)}
										index={index}
										last={orderedTopics.length - 1}
										selected={topic.id === selectedTopicId}
										displayName={
											topic.name.find((n) => n.language === activeLanguageCode)?.name ||
											'Missing translation'
										}
										disableDelete={disableDelete}
										renamePage={renamePage}
										moveUp={() =>
											reorderTopic({
												oldIndex: index,
												oldSectionId: section.id,
												newIndex: index - 1,
												newSectionId: section.id,
											})
										}
										moveDown={() =>
											reorderTopic({
												oldIndex: index,
												oldSectionId: section.id,
												newIndex: index + 1,
												newSectionId: section.id,
											})
										}
									/>
									{/* render a hidden addPage item after the topic if the topic is our selected one */}
									{topic.id === selectedTopicId ? (
										<PageMenuItemAdd
											visible={showAddPage}
											setVisible={setShowAddPage}
											addTopic={addTopic}
											key={`add-${topic.id + ' ' + index}`}
										/>
									) : null}
								</div>
							)
						);
					})}
					{!selectedTopicId || section.topicOrder.length === 0 ? (
						<PageMenuItemAdd
							visible={showAddPage}
							setVisible={setShowAddPage}
							addTopic={addTopic}
							key={`add-zero`}
						/>
					) : null}
				</List>
			</DroppableMenuWrapper>
		)
	);
};
